import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import { Entity } from '@sketchpixy/rubix/lib/L20n';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { RED } from '../../../_config/consts';
import * as ApiKeysAPI from '../../../_config/apiKeysAPI';
import { apiKeyErrorEntity, isAssignableApiKeyRole } from '../../../_config/apiKeysEligibility';
import * as ModalActions from '../../../redux/actions/modal.actions';
import * as RolesActions from '../../../redux/actions/roles.actions';
import * as UtilsActions from '../../../redux/actions/utils.actions';
import MDButton from '../../MDButton/MDButton.jsx';
import FormFlatContainer from '../../forms/Elements/FormFlatContainer.jsx';
import DatePickerView from '../../forms/Fields/DatePickers/SingleDatePicker/DatePickerView.jsx';

const formFieldLabelProps = { style: { fontSize: 16, color: '#3f3f3f' } };
const formFieldInputProps = { style: { fontSize: 16 } };

@connect(state => ({
  roles: state.roles.roles.content,
}))
class ApiKeysSettingsOperationalSection extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      keys: [],
      name: '',
      roleId: '',
      expiresAt: null,
      createdKey: null,
      copied: false,
      isSaving: false,
    };
  }

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(RolesActions.fetchRoles());
    this.loadKeys();
  }

  componentDidUpdate() {
    const { roleId } = this.state;
    if (roleId) return;
    const roles = this.assignableRoles();
    const manager = _.find(roles, role => role.name === 'ROLE_MANAGER');
    const first = manager || _.first(roles);
    if (first) this.setState({ roleId: String(first.id) });
  }

  assignableRoles() {
    const { roles } = this.props;
    return _.map(_.filter(roles, isAssignableApiKeyRole), RolesActions.formatRole);
  }

  showError(error) {
    const { dispatch } = this.props;
    dispatch(ModalActions.showModal({
      modalType: 'ERROR_ALERT',
      modalProps: {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        message: (<h6 className="snack-title"><Entity entity={apiKeyErrorEntity(error)} /></h6>),
      },
    }));
  }

  async loadKeys() {
    const { dispatch } = this.props;
    dispatch(UtilsActions.setSpinnerVisibile(true));
    try {
      const response = await ApiKeysAPI.listApiKeys();
      this.setState({ keys: response.data || [] });
    } catch (error) {
      this.showError(error);
    } finally {
      dispatch(UtilsActions.setSpinnerVisibile(false));
    }
  }

  async onCreate() {
    const { dispatch } = this.props;
    const { name, roleId, expiresAt } = this.state;
    const trimmedName = name.trim();
    const expiresAtMillis = expiresAt || null;
    if (!trimmedName || trimmedName.length > 255 || !roleId || (expiresAtMillis && expiresAtMillis <= Date.now())) {
      this.showError({ response: { data: { code: 13504 } } });
      return;
    }
    this.setState({ isSaving: true });
    dispatch(UtilsActions.setSpinnerVisibile(true));
    try {
      const response = await ApiKeysAPI.createApiKey({
        name: trimmedName,
        roleId: Number(roleId),
        expiresAt: expiresAtMillis,
      });
      this.setState({
        createdKey: response.data && response.data.key,
        copied: false,
        name: '',
        expiresAt: null,
      });
      const list = await ApiKeysAPI.listApiKeys();
      this.setState({ keys: list.data || [] });
    } catch (error) {
      this.showError(error);
    } finally {
      this.setState({ isSaving: false });
      dispatch(UtilsActions.setSpinnerVisibile(false));
    }
  }

  onRevokeRequest(apiKey) {
    const { dispatch } = this.props;
    dispatch(ModalActions.showModal({
      modalType: 'CONFIRM_TO_CONTINUE_MODAL',
      modalProps: {
        title: 'confirmBeforeContinue',
        body: (
          <div>
            <Entity entity="apiKeysRevokeConfirm" />
            <div style={{ marginTop: 8 }}>{apiKey.name}</div>
          </div>
        ),
        onConfirmText: <Entity entity="yes" />,
        onConfirm: () => this.onRevoke(apiKey),
        onCancelText: <Entity entity="no" />,
        onCancel: () => dispatch(ModalActions.hideModal()),
      },
    }));
  }

  async onRevoke(apiKey) {
    const { dispatch } = this.props;
    dispatch(UtilsActions.setSpinnerVisibile(true));
    try {
      await ApiKeysAPI.revokeApiKey(apiKey.id);
      this.setState(state => ({ keys: _.filter(state.keys, key => key.id !== apiKey.id) }));
      dispatch(ModalActions.showModal({
        modalType: 'SUCCESS_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="apiKeysRevokeSuccess" /></h6>),
        },
      }));
    } catch (error) {
      this.showError(error);
    } finally {
      dispatch(UtilsActions.setSpinnerVisibile(false));
    }
  }

  async onCopy() {
    const { createdKey } = this.state;
    try {
      await navigator.clipboard.writeText(createdKey);
      this.setState({ copied: true });
    } catch (error) {
      this.showError(error);
    }
  }

  render() {
    const { keys, name, roleId, expiresAt, createdKey, copied, isSaving } = this.state;
    const roles = this.assignableRoles();

    return (
      <div style={{ paddingLeft: 20, paddingRight: 25 }}>
        {createdKey ? (
          <FormFlatContainer title="apiKeysSecretOnce" containerStyle={{ marginRight: 10, marginTop: 5 }}>
            <div style={{ background: 'white', borderRadius: 8, padding: 12, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 16 }}>
              {createdKey}
            </div>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <MDButton
                containerStyle={{ marginLeft: 0, width: 'auto' }}
                title={<Entity entity={copied ? 'apiKeysCopied' : 'apiKeysCopy'} />}
                onClick={() => this.onCopy()}
              />
              <MDButton
                containerStyle={{ width: 'auto' }}
                backgroundColor="#757575"
                title={<Entity entity="apiKeysHideSecret" />}
                onClick={() => this.setState({ createdKey: null, copied: false })}
              />
            </div>
          </FormFlatContainer>
        ) : null}
        <FormFlatContainer
          title="apiKeysTitle"
          subtitle="apiKeysSubtitle"
          containerStyle={{ marginRight: 10, marginTop: 5 }}
        >
          <TextField
            label={<Entity entity="apiKeysName" />}
            value={name}
            onChange={event => this.setState({ name: event.target.value })}
            inputProps={{ maxLength: 255, style: { fontSize: 16 } }}
            InputLabelProps={formFieldLabelProps}
            InputProps={formFieldInputProps}
            fullWidth
            margin="normal"
          />
          <TextField
            select
            label={<Entity entity="apiKeysRole" />}
            value={roleId}
            onChange={event => this.setState({ roleId: event.target.value })}
            InputLabelProps={formFieldLabelProps}
            InputProps={formFieldInputProps}
            fullWidth
            margin="normal"
          >
            {_.map(roles, role => (
              <MenuItem key={role.id} value={String(role.id)} style={{ fontSize: 16 }}>{role.label}</MenuItem>
            ))}
            {!roleId ? <MenuItem value="" /> : null}
          </TextField>
          <div className="api-keys-expiration-picker" style={{ marginTop: 16, marginBottom: 8 }}>
            <style>{`
              .api-keys-expiration-picker .DateInput_input { font-size: 16px; }
              .api-keys-expiration-picker .SingleDatePickerInput { min-height: 42px; }
            `}</style>
            <h4 style={{ margin: '0 0 10px', color: '#3f3f3f', fontSize: 16, fontWeight: 'bold' }}>
              <Entity entity="apiKeysExpiresOptional" />
            </h4>
            <DatePickerView
              id="api-key-expires-at"
              showClearDate
              showDefaultInputIcon
              placeholder="--/--/----"
              date={expiresAt ? moment(expiresAt) : null}
              isOutsideRange={day => day.isBefore(moment(), 'day')}
              onChange={value => this.setState({
                expiresAt: value ? moment(value).endOf('day').valueOf() : null,
              })}
            />
          </div>
          <MDButton
            containerStyle={{ marginLeft: 0, marginRight: 0 }}
            title={<Entity entity="apiKeysCreate" />}
            disabled={isSaving}
            isLoading={isSaving}
            onClick={() => this.onCreate()}
          />
        </FormFlatContainer>
        <FormFlatContainer containerStyle={{ marginRight: 10 }}>
          {!keys.length ? (
            <h5 style={{ color: '#3f3f3f', marginTop: 0, fontSize: 16 }}><Entity entity="apiKeysEmpty" /></h5>
          ) : _.map(keys, apiKey => (
            <div key={apiKey.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div>
                <h4 style={{ margin: 0, color: '#3f3f3f', fontSize: 18 }}>{apiKey.name}</h4>
                <h5 style={{ margin: '6px 0 0', color: '#3f3f3f', fontWeight: 400, fontSize: 16 }}>
                  <Entity entity="apiKeysPrefix" />
                  {`: ${apiKey.keyPrefix || '—'} · `}
                  {apiKey.roleName || '—'}
                  {' · '}
                  <Entity entity="apiKeysCreated" />
                  {` ${apiKey.createdAt ? moment(apiKey.createdAt).format('LLL') : '—'}`}
                  {' · '}
                  {apiKey.expiresAt ? moment(apiKey.expiresAt).format('LLL') : <Entity entity="apiKeysNeverExpires" />}
                </h5>
              </div>
              <MDButton
                containerStyle={{ margin: 0, width: 'auto' }}
                backgroundColor={RED}
                title={<Entity entity="apiKeysRevoke" />}
                onClick={() => this.onRevokeRequest(apiKey)}
              />
            </div>
          ))}
        </FormFlatContainer>
      </div>
    );
  }
}

export default ApiKeysSettingsOperationalSection;
